// =============================================================================
// siteAccess — which device, which network, which place, on every sign-in
// (JJ, 29 Sep 2026). The tablet half of SITE_ACCESS_SPEC.md §4.
//
// PLAIN ENGLISH. The Command Centre should be able to answer, for every sign-in
// and every PIN action: who, when, which shop, on WHICH DEVICE (a shop tablet or
// someone's own phone), on WHICH NETWORK, and WHERE. And JJ wants phones and
// other non-shop devices kept out unless they are on the shop's own wifi.
//
// The rules live on the server (the shop Worker's /api/access, then the
// `site-access` edge function). This file is only the tablet's side:
//   * a device id — a random id this browser keeps in localStorage
//     ('rolled.device.v1'), made once and never wiped, so the Command Centre can
//     tell one device from another and JJ can mark it "shop tablet";
//   * a plain label for the device from its user agent ('iPhone · Safari');
//   * a GPS fix, asked for in the background and never waited on;
//   * what the browser has said about location (granted / denied / not asked
//     yet / no GPS at all) and how the last GPS ask ended, sent with every check;
//   * accessCheck() — the one call to /api/access, 3 s timeout;
//   * two pure verdict functions that decide what the tablet does with the reply.
//
// THE RULE THAT MATTERS MOST: FAIL OPEN FOR TRADING. The only thing that stops a
// sign-in is the server answering `verdict: "block"`; the only thing that ends a
// session is a heartbeat answered `"signout"` (or `"block"`). A slow, missing,
// broken or unreadable answer — or no answer at all — means carry on exactly as
// the till does today. loginVerdict()/heartbeatVerdict() are where that rule
// lives, and siteAccess.test.mjs throws every non-explicit reply at them.
//
// WHAT NEVER HAPPENS HERE
//   * No PIN is stored. It goes in the body of the one login check and is dropped.
//   * The session token the server hands back is never stored either — the app
//     keeps it in a React ref (memory) and it dies with the page.
//   * The network (IP, ISP, town) is NOT sent from here. The Worker reads it off
//     the connection itself; anything the tablet claimed about its own network
//     would be worth nothing.
//   * No reason is shown to staff. The server sends generic text; if it sends
//     none, GENERIC_BLOCK_MESSAGE below is used — it says nothing about wifi,
//     networks, location or hours, on purpose (spec: "Messages").
//
// WHERE IT RUNS. Only on trafford.eatrolled.com and meadowhall.eatrolled.com,
// or any host with ?access=on (testing). Everywhere else it is inert: no device
// id is made, no GPS is asked for, nothing is posted.
//
// ⚠️ THERE IS NO ?access=off, AND THERE MUST NEVER BE ONE. The people this check
// exists to keep out are staff on their own phones — the exact people who can
// type a URL. A switch-off in the address bar would be a bypass for them. Same
// reasoning as the activity log (activity.js). Switching it off is a deploy (or
// the Command Centre's per-shop rules, which the shop floor can't reach).
// =============================================================================

// ---- constants --------------------------------------------------------------

// localStorage key for the device id. NOT sessionStorage, and NOT on
// sessionLock's SAVED_SESSION_KEYS: the id must survive the session wipe, every
// logout and every idle lock, or a shop tablet would look like a new device each
// time someone signed in. (Pinned by siteAccess.test.mjs.)
export const DEVICE_ID_KEY = "rolled.device.v1";

// The only hosts where this runs without ?access=on. Closed shops (Arndale, the
// Manchester kitchen) are deliberately absent — the spec forbids adding rules or
// blocking for them.
export const ACCESS_HOSTS = ["trafford.eatrolled.com", "meadowhall.eatrolled.com"];

export const ACCESS_URL = "/api/access";            // same origin: the shop Worker
export const ACCESS_TIMEOUT_MS = 3000;               // spec §4: 3 s, then fail open
export const HEARTBEAT_MS = 60 * 1000;               // spec §4: every 60 s while signed in
export const GPS_REFRESH_MS = 5 * 60 * 1000;         // spec §4: every 5 min while signed in
export const GPS_OPTIONS = { enableHighAccuracy: true, timeout: 8000, maximumAge: 300000 };
// A GPS ask that is still waiting after this long (e.g. a permission prompt
// nobody answered) no longer holds back the next ask.
const GPS_PENDING_MAX_MS = 60 * 1000;
// A fix older than this is never sent. A page can stay alive in a phone's memory
// for hours: without this, a fix taken at the shop would be sent with a sign-in
// made at home and pin the home sign-in on the shop — the exact case this exists
// to catch. No fix beats a wrong one (the server still has the network location).
export const GPS_MAX_AGE_MS = 10 * 60 * 1000;
// The PIN pad asks for a fresh fix every time it shows — after a lock, a reopen,
// or the next person — but not more than once in this long.
export const GPS_PAD_ASK_MIN_MS = 2 * 60 * 1000;
// A server message longer than this is cut — it is only ever a sentence.
const MESSAGE_MAX_CHARS = 300;

// The spec's own block text, used ONLY when a block/sign-out reply arrives with
// no message of its own. Generic by design: never mentions wifi, networks,
// location or hours.
export const GENERIC_BLOCK_MESSAGE =
  "You can't use the site app on this device right now. Please use the shop tablet or speak to a manager.";

// ---- the host gate ----------------------------------------------------------

// Pure, so the test can pin it. `query` is the raw ?access= value, or null.
// Only "on" does anything; any other value (including "off") leaves the host's
// own answer standing — see the ⚠️ in the header.
export function accessOnFor({ hostname = "", query = null } = {}) {
  if (query === "on") return true;
  return ACCESS_HOSTS.includes(String(hostname || "").toLowerCase());
}

export function siteAccessEnabled() {
  if (typeof window === "undefined" || !window || !window.location) return false; // node/tests: off
  let q = null;
  try { q = new URLSearchParams(window.location.search || "").get("access"); } catch (e) { q = null; }
  return accessOnFor({ hostname: window.location.hostname || "", query: q });
}

// ---- the device id ----------------------------------------------------------

let memoDeviceId = null;   // this page's id, used when storage is blocked or gets cleared mid-page

const DEVICE_ID_RE = /^[A-Za-z0-9-]{8,64}$/;
export function isValidDeviceId(v) {
  return typeof v === "string" && DEVICE_ID_RE.test(v);
}

// A fresh random id: crypto.randomUUID() where the browser has it, else a v4
// UUID from crypto.getRandomValues, else (very old WebViews) Math.random.
export function makeDeviceId() {
  try {
    const c = typeof crypto !== "undefined" ? crypto : null;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    if (c && typeof c.getRandomValues === "function") {
      const b = new Uint8Array(16);
      c.getRandomValues(b);
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
      return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
    }
  } catch (e) { /* fall through */ }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === "x" ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

function defaultStorage() {
  try { return typeof window !== "undefined" && window && window.localStorage ? window.localStorage : null; } catch (e) { return null; }
}

// This device's id: read from localStorage, made (once) if it isn't there.
// Never throws. With storage blocked the id still stays the same for the life of
// the page; it just can't outlive it.
export function deviceId({ storage = defaultStorage(), newId = makeDeviceId } = {}) {
  let stored = null;
  try { stored = storage ? storage.getItem(DEVICE_ID_KEY) : null; } catch (e) { stored = null; }
  if (isValidDeviceId(stored)) { memoDeviceId = stored; return stored; }
  let id = isValidDeviceId(memoDeviceId) ? memoDeviceId : null;
  if (!id) {
    try { id = newId(); } catch (e) { id = null; }
    if (!isValidDeviceId(id)) id = makeDeviceId();
  }
  try { if (storage) storage.setItem(DEVICE_ID_KEY, id); } catch (e) { /* blocked: keep it for this page */ }
  memoDeviceId = id;
  return id;
}

// ---- what the device is -----------------------------------------------------

// { class, label } from a user agent. Pure. The classification is the same idea
// as getDeviceInfo() in foh.jsx: an iPad on iPadOS 13+ says "Macintosh", so a
// Mac with a touch screen is an iPad; Android without "Mobile" is a tablet.
// Screen width is only used when the system can't be recognised at all.
//   class: 'tablet' | 'mobile' | 'desktop'
//   label: 'iPhone · Safari', 'Android tablet · Chrome', 'Windows PC · Edge' …
export function describeDevice({ ua = "", maxTouchPoints = 0, width = 0 } = {}) {
  const s = String(ua || "");
  const touch = Number(maxTouchPoints) || 0;
  const w = Number(width) || 0;

  let kind = null, cls = null;
  if (/iPad/.test(s) || (/Macintosh/.test(s) && touch > 1)) { kind = "iPad"; cls = "tablet"; }
  else if (/iPhone/.test(s)) { kind = "iPhone"; cls = "mobile"; }
  else if (/iPod/.test(s)) { kind = "iPod"; cls = "mobile"; }
  else if (/Windows Phone|IEMobile/.test(s)) { kind = "Windows phone"; cls = "mobile"; }
  else if (/Android/.test(s)) {
    if (/Mobile/.test(s)) { kind = "Android phone"; cls = "mobile"; }
    else { kind = "Android tablet"; cls = "tablet"; }
  }
  else if (/Windows/.test(s)) { kind = "Windows PC"; cls = "desktop"; }
  else if (/CrOS/.test(s)) { kind = "Chromebook"; cls = "desktop"; }
  else if (/Macintosh|Mac OS X/.test(s)) { kind = "Mac"; cls = "desktop"; }
  else if (/webOS|BlackBerry|Opera Mini|Mobi/i.test(s)) { kind = "Phone"; cls = "mobile"; }
  else if (/Linux|X11/.test(s)) { kind = "Linux PC"; cls = "desktop"; }
  if (!cls) {
    kind = "Unknown device";
    cls = (w && w < 600) ? "mobile" : ((w && w < 1100 && /Tablet|Touch/i.test(s)) ? "tablet" : "desktop");
  }

  const apple = /iPhone|iPad|iPod|Macintosh/.test(s);
  let browser;
  if (/FBAN|FBAV|FB_IAB|Instagram|Snapchat|LinkedInApp|musical_ly|TikTok/.test(s)) browser = "In-app browser";
  else if (/EdgA\/|EdgiOS\/|Edg\/|Edge\//.test(s)) browser = "Edge";
  else if (/SamsungBrowser\//.test(s)) browser = "Samsung Internet";
  else if (/OPR\/|OPT\/|OPiOS\/|Opera/.test(s)) browser = "Opera";
  else if (/Firefox\/|FxiOS\//.test(s)) browser = "Firefox";
  else if (/DuckDuckGo\/|Ddg\//.test(s)) browser = "DuckDuckGo";
  else if (/GSA\//.test(s)) browser = "Google app";
  else if (/CriOS\//.test(s)) browser = "Chrome";
  else if (/; wv\)/.test(s)) browser = "In-app browser";          // Android WebView (kiosk shells, in-app links)
  else if (/Chrome\/|Chromium\//.test(s)) browser = "Chrome";
  else if (apple && /Safari\//.test(s)) browser = "Safari";
  else if (apple && /AppleWebKit/.test(s)) browser = "In-app browser"; // iOS web view: no Safari token
  else browser = "Browser";

  return { class: cls, label: `${kind} · ${browser}` };
}

// This browser, described.
export function deviceInfo() {
  let ua = "", touch = 0, width = 0;
  try {
    if (typeof navigator !== "undefined" && navigator) {
      ua = String(navigator.userAgent || "");
      touch = Number(navigator.maxTouchPoints) || 0;
    }
  } catch (e) { /* unreadable: describe as unknown */ }
  try { if (typeof window !== "undefined" && window) width = Number(window.innerWidth) || 0; } catch (e) {}
  return describeDevice({ ua, maxTouchPoints: touch, width });
}

// The meta every presence tap puts on its activity row, so each action links to
// its device (spec: "Every action links to its device"). null where site access
// is off, so every other host writes exactly what it wrote before.
export function activityMeta() {
  try {
    if (!siteAccessEnabled()) return null;
    return { device_id: deviceId(), device: deviceInfo().label };
  } catch (e) { return null; }
}

// ---- GPS --------------------------------------------------------------------
// Asked for in the background and NEVER waited on: a check sends whatever fix is
// already held (or null). The fix is where the device is, not who is using it,
// so it is kept across sign-ins for the life of the page.

let heldFix = null;          // { lat, lng, accuracy, at }
let gpsPendingSince = 0;     // when the ask that hasn't answered yet was made
let gpsPadAskedAt = 0;       // when the PIN pad last asked

// A browser Position → the fix we send. Pure; null for anything unusable.
export function fixFromPosition(pos) {
  const c = pos && pos.coords;
  if (!c) return null;
  const lat = Number(c.latitude), lng = Number(c.longitude), acc = Number(c.accuracy);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  const t = Number(pos.timestamp);
  return {
    lat, lng,
    accuracy: Number.isFinite(acc) && acc >= 0 ? acc : null,
    at: new Date(Number.isFinite(t) && t > 0 ? t : Date.now()).toISOString(),
  };
}

function defaultGeo() {
  try { return typeof navigator !== "undefined" && navigator && navigator.geolocation ? navigator.geolocation : null; } catch (e) { return null; }
}

// Ask for a fix now (one ask at a time). Returns true if an ask was made.
// Never throws; a refusal or error keeps whatever fix was already held. Every
// answer also updates the location permission/error below; a browser with no
// geolocation at all is recorded as "unsupported".
export function requestGpsFix({ geo = defaultGeo(), now = () => Date.now() } = {}) {
  try {
    if (!geo || typeof geo.getCurrentPosition !== "function") { locPermission = "unsupported"; return false; }
    const t = now();
    if (gpsPendingSince && t >= gpsPendingSince && t - gpsPendingSince < GPS_PENDING_MAX_MS) return false;
    gpsPendingSince = t;
    geo.getCurrentPosition(
      (pos) => { gpsPendingSince = 0; const f = fixFromPosition(pos); if (f) heldFix = f; noteGpsSuccess(); },
      (err) => { gpsPendingSince = 0; noteGpsError(err); },
      GPS_OPTIONS,
    );
    return true;
  } catch (e) {
    gpsPendingSince = 0;
    return false;
  }
}

// The PIN pad's ask: every time it shows, at most once per GPS_PAD_ASK_MIN_MS, so
// the fix sent with a sign-in is from now, not from whenever the page was opened.
export function askGpsOnPad({ geo, now = () => Date.now() } = {}) {
  const t = now();
  if (gpsPadAskedAt && t >= gpsPadAskedAt && t - gpsPadAskedAt < GPS_PAD_ASK_MIN_MS) return false;
  gpsPadAskedAt = t;
  return requestGpsFix({ geo, now }); // geo undefined → the browser's own
}

// The fix held right now (a copy), or null — also null when it is older than
// GPS_MAX_AGE_MS or dated more than 2 minutes ahead (a clock that can't be trusted).
export function currentGpsFix({ now = Date.now() } = {}) {
  if (!heldFix) return null;
  const age = now - Date.parse(heldFix.at);
  if (!Number.isFinite(age) || age > GPS_MAX_AGE_MS || age < -2 * 60 * 1000) return null;
  return { ...heldFix };
}

// ---- the location permission (JJ, 29 Sep 2026: "log the answer") -------------
// What this browser has said about location, sent with EVERY check as
// `location: { permission, error }`, so the Command Centre can tell "no pin
// because they said no" from "no pin because the GPS timed out indoors".
//   permission  "granted" | "denied" | "prompt" | "unsupported" | "unknown".
//               Two sources, the latest answer wins:
//                 * the Permissions API — navigator.permissions.query({ name:
//                   "geolocation" }), then its `change` event (watchLocationPermission);
//                 * our own getCurrentPosition answers — a fix → "granted",
//                   PERMISSION_DENIED (code 1) → "denied".
//               No navigator.geolocation → "unsupported". Before anything is
//               known (or a state we don't recognise) → "unknown".
//   error       "timeout" (code 3) | "unavailable" (code 2) | null — how the MOST
//               RECENT getCurrentPosition ask ended; a fix clears it, and so does a
//               refusal (that one is told by `permission`).
// Never throws and never waits: a check sends whatever is known at that moment.

export const LOCATION_PERMISSIONS = ["granted", "denied", "prompt", "unsupported", "unknown"];
export const LOCATION_ERRORS = ["timeout", "unavailable"];

let locPermission = "unknown";
let locError = null;
let permWatchStarted = false;
let permStatusHeld = null;   // kept so the browser goes on delivering its `change` events

// A Permissions API state → ours. Pure.
export function permissionFromState(state) {
  return state === "granted" || state === "denied" || state === "prompt" ? state : "unknown";
}

// A GeolocationPositionError code → the `error` we send. Pure. (Code 1, a
// refusal, is not an error here: it sets permission to "denied" instead.)
export function errorFromCode(code) {
  const c = Number(code);
  return c === 3 ? "timeout" : c === 2 ? "unavailable" : null;
}

function noteGpsSuccess() {
  locPermission = "granted";
  locError = null;
}

function noteGpsError(err) {
  let code = null;
  try { code = err ? Number(err.code) : null; } catch (e) { code = null; }
  if (code === 1) { locPermission = "denied"; locError = null; return; }
  locError = errorFromCode(code);
}

function defaultPermissions() {
  try { return typeof navigator !== "undefined" && navigator && navigator.permissions ? navigator.permissions : null; } catch (e) { return null; }
}

// Start watching the browser's geolocation permission (once per page). Returns
// true if a Permissions API query was started. No geolocation → "unsupported";
// no Permissions API (older Safari), or a query it refuses → left as it is, and
// the GPS answers fill it in. Never throws; the query is never waited on.
export function watchLocationPermission({ geo = defaultGeo(), permissions = defaultPermissions() } = {}) {
  try {
    if (!geo || typeof geo.getCurrentPosition !== "function") { locPermission = "unsupported"; return false; }
    if (permWatchStarted) return false;
    if (!permissions || typeof permissions.query !== "function") return false;
    permWatchStarted = true;
    Promise.resolve()
      .then(() => permissions.query({ name: "geolocation" }))
      .then((status) => {
        if (!status || typeof status !== "object") return;
        permStatusHeld = status;
        locPermission = permissionFromState(status.state);
        const onChange = () => { try { locPermission = permissionFromState(status.state); } catch (e) { /* keep the last known */ } };
        if (typeof status.addEventListener === "function") status.addEventListener("change", onChange);
        else status.onchange = onChange;
      })
      .catch(() => { /* "geolocation" not queryable here: the GPS answers still fill it in */ });
    return true;
  } catch (e) {
    return false;
  }
}

// What we know right now (a fresh object each time).
export function locationState() {
  return { permission: locPermission, error: locError };
}

// Anything passed in explicitly (tests) is held to the contract's vocabulary.
function normalizeLocation(loc) {
  const p = loc && typeof loc === "object" ? loc.permission : null;
  const e = loc && typeof loc === "object" ? loc.error : null;
  return {
    permission: LOCATION_PERMISSIONS.includes(p) ? p : "unknown",
    error: LOCATION_ERRORS.includes(e) ? e : null,
  };
}

// ---- the check itself -------------------------------------------------------

const unreachable = () => ({ unreachable: true });

// The body POSTed to /api/access (spec §2). The Worker adds `net` and `ua` from
// the connection itself; the tablet never sends either. A heartbeat always
// carries `session` — null when the sign-in got none (the check was unreachable
// then): the server can't end a session for lacking a token, but lockdown, a
// blocked device and the shop-wifi rule still reach that device (review, 29 Sep).
// Every body — login and heartbeat — carries `location: { permission, error }`
// (see "the location permission" above).
export function buildAccessBody({ event, site, pin, session } = {}, { device = null, gps, location } = {}) {
  let dev = device;
  if (!dev) {
    const info = deviceInfo();
    dev = { id: deviceId(), class: info.class, label: info.label };
  }
  const body = {
    event,
    site,
    device: dev,
    gps: gps === undefined ? currentGpsFix() : (gps || null),
    location: location === undefined ? locationState() : normalizeLocation(location),
  };
  if (event === "login") body.pin = pin;
  if (event === "heartbeat") body.session = typeof session === "string" && session ? session : null;
  return body;
}

function defaultFetch(url, init) { return fetch(url, init); }

// accessCheck({ event: 'login', site, pin }) / ({ event: 'heartbeat', site, session })
// (session may be null — see buildAccessBody)
// → the server's parsed JSON reply, or { unreachable: true } on ANY failure:
// no network, a timeout (ACCESS_TIMEOUT_MS), a non-JSON answer (e.g. the page's
// own 404 before the Worker is live), a JSON answer that isn't an object, or a
// throw anywhere in here. Never rejects, never throws.
export function accessCheck(args, { fetchFn = defaultFetch, timeoutMs = ACCESS_TIMEOUT_MS, device, gps, location } = {}) {
  return new Promise((resolve) => {
    let done = false, timer = null, ctrl = null;
    const finish = (v) => {
      if (done) return;
      done = true;
      if (timer != null) { try { clearTimeout(timer); } catch (e) {} }
      resolve(v);
    };
    try {
      const body = JSON.stringify(buildAccessBody(args || {}, { device, gps, location }));
      try { ctrl = typeof AbortController !== "undefined" ? new AbortController() : null; } catch (e) { ctrl = null; }
      timer = setTimeout(() => {
        try { if (ctrl) ctrl.abort(); } catch (e) {}
        finish(unreachable());
      }, timeoutMs);
      Promise.resolve()
        .then(() => fetchFn(ACCESS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          cache: "no-store",
          credentials: "same-origin",
          signal: ctrl ? ctrl.signal : undefined,
        }))
        .then((r) => r.text())
        .then((text) => {
          const reply = JSON.parse(text);
          finish(reply && typeof reply === "object" && !Array.isArray(reply) ? reply : unreachable());
        })
        .catch(() => finish(unreachable()));
    } catch (e) {
      finish(unreachable());
    }
  });
}

// ---- what the tablet does with a reply (pure; fail open) --------------------

// Login: 'block' ONLY for an explicit, well-formed block. Everything else —
// unreachable, ok:false, a missing/unknown verdict, anything odd — is 'allow'.
export function loginVerdict(reply) {
  return reply && typeof reply === "object" && reply.ok === true && reply.verdict === "block" ? "block" : "allow";
}

// Heartbeat: 'signout' ONLY for an explicit signout (or block). Everything else
// keeps the session going.
export function heartbeatVerdict(reply) {
  return reply && typeof reply === "object" && reply.ok === true &&
    (reply.verdict === "signout" || reply.verdict === "block") ? "signout" : "allow";
}

// The text to show staff on a block / sign-out: the server's own (already
// generic) message, else the generic one. Never an internal reason.
export function staffMessage(reply) {
  const m = reply && typeof reply.message === "string" ? reply.message.trim() : "";
  return m ? m.slice(0, MESSAGE_MAX_CHARS) : GENERIC_BLOCK_MESSAGE;
}

// The session token from an allowed login reply, or null. The caller keeps it in
// memory only (a React ref), never in storage.
export function sessionFrom(reply) {
  const s = reply && reply.ok === true ? reply.session : null;
  return typeof s === "string" && s.length > 0 && s.length <= 4096 ? s : null;
}

// Test seam — a clean slate between tests.
export function _resetSiteAccessForTests() {
  memoDeviceId = null;
  heldFix = null;
  gpsPendingSince = 0;
  gpsPadAskedAt = 0;
  locPermission = "unknown";
  locError = null;
  permWatchStarted = false;
  permStatusHeld = null;
}
